import React, { useEffect, useState } from "react";
import Layout from "@/components/layout/Layout";
import MainHeading from "@/components/common/MainHeading";
import Card from "@/components/common/Card";
import { Button } from "@/components/ui/button";
import { useNavigate, useParams } from "react-router-dom";
import { ThrowError } from "@/api/callApi";
import moment from "moment";
import { ArrowLeft, Calendar, FileText, Info, User } from "lucide-react";
import type { RegularizationItem } from "@/interface/regularization.interface";
import { RegularizationService } from "@/services/Regularization/RegularizationServices";
import {
  showToastNotification,
  formatStatus,
  formatTimeHM,
} from "@/utility/helper";
import { RoutesLink } from "@/utility/RouteLinks";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import FullPageLoader from "@/components/common/FullPageLoader";

const RegularizationDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { siteId } = useCurrentUser();

  const [item, setItem] = useState<RegularizationItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // For admin actions
  const [remarks, setRemarks] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isTeamRegularization = location.pathname.includes("team-regularization-detail");

  // const isAdmin = Number(user?.type) === 1;
  // const hasArPermission =
  //   user?.designation?.menuBar?.keyNames?.includes(
  //     permissionKeys.regularization, 
  //   );
  // const canApproveReject = isAdmin || hasArPermission;

  const fetchData = async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await RegularizationService.getById(id, siteId);
      if (response?.status && response.data) {
        setItem(response.data);
        if (response.data.remarks) {
          setRemarks(response.data.remarks);
        }
      } else {
        setError("Regularization details not found");
      }
    } catch (err) {
      ThrowError(err);
      setError("Failed to load regularization details");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleUpdateStatus = async (newStatus: "APPROVED" | "REJECTED") => {
    if (!id) return;
    try {
      setIsSubmitting(true);
      const payload = {
        status: newStatus,
        remarks: remarks,
        siteId: siteId,
      };
      const response = await RegularizationService.updateStatus(id, payload);
      if (response?.status) {
        showToastNotification(
          "success",
          response?.message ||
          `Regularization ${newStatus.toLowerCase()} successfully`,
        );
        navigate(RoutesLink.teamRegularization)
        // fetchData();
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <FullPageLoader />
        </div>
      </Layout>
    );
  }

  if (error || !item) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h3 className="text-lg font-semibold text-gray-700">
            {error || "Regularization details not found"}
          </h3>
          <Button onClick={() => navigate(-1)} className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </Layout>
    );
  }

  const status = item.status?.toLowerCase() || "pending";
  let statusClasses = "text-amber-700 bg-amber-50 border-amber-200";
  if (status === "approved") {
    statusClasses = "text-green-700 bg-green-50 border-green-200";
  } else if (status === "rejected" || status === "cancelled") {
    statusClasses = "text-destructive bg-destructive/10 border-destructive/20";
  }

  const isPending = item.status === "PENDING";

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <MainHeading
            title="Regularization Detail"
            isBackButtonVisible
            onClick={() => navigate(-1)}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100">
                <Info className="w-5 h-5 text-primary" />
                <h3 className="font-display text-base font-semibold">
                  Regularization Information
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                <InfoItem
                  label="Check-in Date"
                  value={
                    item.checkInDate
                      ? moment(item.checkInDate).format("DD-MM-YYYY")
                      : "—"
                  }
                />
                <InfoItem
                  label="Check-out Date"
                  value={
                    item.checkOutDate
                      ? moment(item.checkOutDate).format("DD-MM-YYYY")
                      : "—"
                  }
                />
                <InfoItem label="First half Check-in Time" value={formatTimeHM(item.firstHalfCheckInTime)} />
                <InfoItem label="First half Check-out Time" value={formatTimeHM(item.firstHalfCheckOutTime)} />
                <InfoItem label="Second half Check-in Time" value={formatTimeHM(item.secondHalfCheckInTime)} />
                <InfoItem label="Second half Check-out Time" value={formatTimeHM(item.secondHalfCheckOutTime)} />
                <InfoItem label="Reason" value={item.reason} />
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                    Status
                  </p>
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold tracking-wide border ${statusClasses}`}
                  >
                    {formatStatus(item.status || "PENDING")}
                  </span>
                </div>
              </div>
            </Card>

            {item.remarks && status !== "pending" && (
              <Card>
                <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
                  <FileText className="w-5 h-5 text-primary" />
                  <h3 className="font-display text-base font-semibold">
                    Admin Remarks
                  </h3>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed bg-slate-50 p-4 rounded-xl border border-slate-100">
                  {item.remarks}
                </p>
              </Card>
            )}


            {isTeamRegularization && isPending && (
              <Card className="border border-slate-100 bg-linear-to-b from-white to-slate-50/20">
                <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
                  <Info className="w-5 h-5 text-primary" />
                  <h3 className="font-display text-base font-semibold">
                    Review Actions
                  </h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">
                      Admin Remarks
                    </label>
                    <textarea
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      placeholder="Enter remarks for approval or rejection..."
                      className="w-full min-h-[90px] text-sm p-3 bg-white border border-slate-200 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
                    />
                  </div>
                  <div className="flex items-center justify-end gap-3 font-semibold">
                    <Button
                      variant="destructive"
                      className="px-5 py-2 h-10 shadow-sm"
                      disabled={isSubmitting}
                      onClick={() => handleUpdateStatus("REJECTED")}
                    >
                      Reject
                    </Button>
                    <Button
                      className="px-5 py-2 h-10 shadow-sm"
                      disabled={isSubmitting}
                      onClick={() => handleUpdateStatus("APPROVED")}
                    >
                      Approve
                    </Button>
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card>
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100">
                <User className="w-5 h-5 text-primary" />
                <h3 className="font-display text-base font-semibold">
                  Applicant Details
                </h3>
              </div>
              <div className="space-y-4">
                <InfoItem
                  label="Employee Name"
                  value={item.user?.name || "—"}
                />
                <InfoItem
                  label="Email Address"
                  value={item.user?.email || "—"}
                />
                <InfoItem label="Phone" value={item.user?.phone || "—"} />
              </div>
            </Card>
            <Card>
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100">
                <User className="w-5 h-5 text-primary" />
                <h3 className="font-display text-base font-semibold">
                  Action Takken
                </h3>
              </div>
              <div className="space-y-4">
                <InfoItem
                  label="Employee Name"
                  value={item.actionUser?.name || "—"}
                />
                <InfoItem
                  label="Email Address"
                  value={item.actionUser?.email || "—"}
                />
                <InfoItem label="Phone" value={item.actionUser?.phone || "—"} />
              </div>
            </Card>

            <Card>
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100">
                <Calendar className="w-5 h-5 text-primary" />
                <h3 className="font-display text-base font-semibold">
                  Timeline
                </h3>
              </div>
              <div className="space-y-4">
                <InfoItem
                  label="Applied On"
                  value={
                    item.createdAt
                      ? moment(item.createdAt).format("DD-MM-YYYY HH:mm")
                      : "—"
                  }
                />
                {item.updatedAt && item.updatedAt !== item.createdAt && (
                  <InfoItem
                    label="Last Updated"
                    value={moment(item.updatedAt).format("DD-MM-YYYY HH:mm")}
                  />
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

const InfoItem: React.FC<{ label: string; value?: string | number | null }> = ({
  label,
  value,
}) => (
  <div>
    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
      {label}
    </p>
    <p className="text-sm font-semibold text-foreground ">
      {value || "—"}
    </p>
  </div>
);

export default RegularizationDetails;
